/**
 * La chiave di un promemoria, e perché non è l'identificativo del programma.
 *
 * ## Il difetto che questa funzione chiude
 *
 * `ProgramStore.materialize` **sintetizza** l'identificativo: `${channelId}-${slot}`. È un indice di
 * posizione dentro il magazzino, non l'id che l'EPG ha mandato — quello viene buttato via quando i
 * programmi entrano negli array tipizzati, perché lì dentro ci stanno solo numeri.
 *
 * Dall'altra parte, `line.app_data.reminders` conserva gli identificativi **veri**, quelli letti da
 * `get_simple_data_table`. Le due forme non possono coincidere: `reminderIds.has(program.id)` era
 * falso sempre, per costruzione. L'anteprima della guida scriveva «Imposta promemoria» anche su un
 * programma che ne aveva uno, e l'unico modo di sapere se era stato salvato era rimetterlo.
 *
 * ## Canale più istante, e non l'id dell'EPG
 *
 * Anche avendo l'id vero non andrebbe bene: **cambia a ogni import**. Le fonti EPG rinumerano, e un
 * promemoria messo stasera punterebbe a un programma diverso domani — o a nessuno. Canale e istante
 * d'inizio sono invece quel che la persona ha scelto davvero: «questo canale, a quest'ora».
 *
 * Il campo resta `programId` nel contratto perché è il nome che ha, e nessun lettore di terze parti
 * guarda dentro `line.app_data`: quel che c'è scritto lo scriviamo e lo leggiamo noi.
 */
export function reminderKey(channelId: string, startMs: number): string {
  return `${channelId}@${startMs}`;
}

/** Il canale e l'istante che una chiave contiene. */
export interface ReminderTarget {
  readonly channelId: string;
  readonly startMs: number;
}

/**
 * Canale e istante **riletti dalla chiave**, o `null` se non è una chiave nostra.
 *
 * ## Perché serve leggerla al contrario
 *
 * Perché la chiave è l'unica cosa che c'è sempre. Il palinsesto in memoria è una **finestra** — si
 * chiede un canale per volta, quando il dito si ferma — e un promemoria messo dall'hero su una
 * partita di giovedì non ha nessun `Program` caricato: la pagina dei promemoria, che li componeva
 * dai programmi, non lo mostrava **mai**. Canale e ora invece sono qui dentro, e bastano a dire
 * «questo canale, a quest'ora».
 *
 * ## Si taglia all'ultima chiocciola
 *
 * L'identificativo di un canale arriva da fonti altrui e non promette di non contenere una `@`;
 * l'istante, in fondo, sono solo cifre. Tagliare alla **prima** chiocciola romperebbe proprio quei
 * canali, e in modo silenzioso: un promemoria che non compare.
 *
 * Una chiave in cui l'istante non è un numero intero torna `null` e **non si tocca**: sono le
 * chiavi del formato di prima (`canale-slot`, l'id sintetico del magazzino), e non si cancellano
 * solo perché non le sappiamo più leggere.
 */
export function parseReminderKey(key: string): ReminderTarget | null {
  const at = key.lastIndexOf('@');
  if (at <= 0 || at === key.length - 1) return null;

  const raw = key.slice(at + 1);
  // Solo cifre: `Number('12x')` è `NaN`, e un `NaN` qui diventerebbe un promemoria che non scade mai
  // e non si ordina.
  if (!/^[0-9]+$/.test(raw)) return null;

  return { channelId: key.slice(0, at), startMs: Number(raw) };
}

/** Vero se per quel canale, a quell'istante, un promemoria è già stato messo. */
export function hasReminder(
  reminderIds: ReadonlySet<string>,
  channelId: string,
  startMs: number,
): boolean {
  return reminderIds.has(reminderKey(channelId, startMs));
}
